import React, { useState, useEffect } from "react";
import { Bath, Clock, Shield, Star, Wrench, CheckCircle, Phone, Mail, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

const RifacimentiBagniSection = () => {
  const [currentImage, setCurrentImage] = useState(0);

  const bathroomImages = [
    {
      url: "/images/serramenti2.jpg",
      title: "Bagno Moderno Premium",
      description: "Box doccia in cristallo temperato con rivestimenti in pietra naturale e illuminazione LED integrata"
    },
    {
      url: "/images/serramenti1.jpg",
      title: "Design Contemporaneo",
      description: "Mobile sospeso con doppio lavabo, pavimenti effetto legno e specchiera retroilluminata"
    },
    {
      url: "/images/bagno6.jpeg",
      title: "Trasformazione in Corso",
      description: "Lavorazione di precisione con materiali di alta qualità e cura artigianale"
    },
    {
      url: "/images/bagno3.jpeg",
      title: "Doccia Walk-in Minimalista",
      description: "Soluzione salvaspazio con piatto doccia filo pavimento e rubinetteria di design"
    },
    {
      url: "/images/bagno1.jpeg",
      title: "Bagno Completo Luxury",
      description: "Ristrutturazione totale con finiture di lusso e tecnologie all'avanguardia"
    }
  ];

  // Auto-slide ogni 5 secondi
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % bathroomImages.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [bathroomImages.length]);

  const goToPrevious = () => {
    setCurrentImage((prev) => (prev - 1 + bathroomImages.length) % bathroomImages.length);
  };

  const goToNext = () => {
    setCurrentImage((prev) => (prev + 1) % bathroomImages.length);
  };

  const features = [
    "Progettazione 3D gratuita e personalizzata",
    "Demolizione controllata e smaltimento sicuro",
    "Rifacimento completo impianti idraulici ed elettrici",
    "Installazione sanitari di design e alta qualità",
    "Rivestimenti ceramici, pietra naturale e resina",
    "Box doccia su misura in cristallo temperato",
    "Sistemi di riscaldamento a pavimento radiante",
    "Illuminazione LED integrata e controllo smart",
    "Accessori e rubinetteria di design italiano",
    "Garanzia 5 anni su tutti i lavori eseguiti"
  ];

  const benefits = [
    "Trasformazione da vasca in doccia in soli 8 ore",
    "Nessun danno alle piastrelle esistenti",
    "Risultato estetico superiore e moderno",
    "Maggiore sicurezza e praticità d'uso",
    "Aumento del valore dell'immobile",
    "Risparmio energetico e idrico",
    "Facilità di pulizia e manutenzione",
    "Personalizzazione totale di stili e materiali"
  ];

  return (
    <section id="rifacimenti-dettagli" className="py-20 lg:py-32 bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-4 lg:px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <Bath className="h-16 w-16 text-blue-600 mx-auto mb-4" />
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Rifacimenti <span className="text-blue-600">Bagni-Docce</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed text-justify">
            <strong>Trasformiamo il tuo bagno in un'oasi di comfort e design.</strong><br />
            Dalla progettazione 3D alla realizzazione finale, ci occupiamo di ogni dettaglio 
            per creare il bagno dei tuoi sogni con materiali di lusso e tecnologie all'avanguardia.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Dynamic Image Slider */}
          <div className="relative">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl relative">
              {/* Images */}
              {bathroomImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
                    index === currentImage
                      ? 'opacity-100 scale-100'
                      : 'opacity-0 scale-105'
                  }`}
                >
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover"
                  />
                  {/* Overlay elegante */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-8 left-8 right-8 text-white">
                      <h4 className="text-xl font-bold mb-3">{image.title}</h4>
                      <p className="text-sm text-gray-200 leading-relaxed">{image.description}</p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Navigation Controls */}
              <button
                onClick={goToPrevious}
                className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-blue-600/80 hover:bg-blue-700 text-white p-3 rounded-full transition-all duration-300 hover:scale-110 shadow-lg"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              
              <button
                onClick={goToNext}
                className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-blue-600/80 hover:bg-blue-700 text-white p-3 rounded-full transition-all duration-300 hover:scale-110 shadow-lg"
              >
                <ChevronRight className="h-6 w-6" />
              </button>

              {/* Elegant Dot Indicators */}
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3">
                {bathroomImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImage(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentImage
                        ? 'bg-blue-400 scale-125 shadow-lg'
                        : 'bg-white/60 hover:bg-white/80'
                    }`}
                  />
                ))}
              </div>

              {/* Image Counter */}
              <div className="absolute top-6 right-6 bg-black/40 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
                {currentImage + 1} / {bathroomImages.length}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* Key Info Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="text-center">
                <CardContent className="p-6">
                  <Clock className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900 mb-1">8-15</div>
                  <div className="text-sm text-gray-600">Giorni Lavorativi</div>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <Shield className="h-8 w-8 text-green-600 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900 mb-1">5 Anni</div>
                  <div className="text-sm text-gray-600">Garanzia</div>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <Star className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900 mb-1">Premium</div>
                  <div className="text-sm text-gray-600">Materiali</div>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Description */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">La Nostra Esperienza nel Rifacimento Bagni</h3>
              <p className="text-gray-700 leading-relaxed mb-6 text-justify">
                Con <strong>oltre 18.000 progetti completati</strong>, Star Italia è leader nel rifacimento bagni in Italia. 
                Trasformiamo il tuo bagno in uno spazio moderno, funzionale e di design, utilizzando materiali di 
                prima qualità e tecnologie innovative. Ogni progetto è unico e personalizzato secondo le tue esigenze 
                e il tuo stile di vita.
              </p>
              <p className="text-gray-700 leading-relaxed text-justify">
                Dalla <strong>trasformazione da vasca in doccia</strong> alla ristrutturazione completa, 
                offriamo soluzioni chiavi in mano con un unico interlocutore che si occupa di tutto: 
                pratiche burocratiche, progettazione, fornitura materiali e realizzazione.
              </p>
            </div>

            {/* Features List */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Cosa Include il Nostro Servizio</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Vantaggi della Trasformazione</h3>
              <div className="space-y-3">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Star className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Pronto per Trasformare il Tuo Bagno?</h3>
              <p className="text-blue-100 mb-6">
                Contattaci per un sopralluogo gratuito e un preventivo personalizzato senza impegno.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-white text-blue-600 hover:bg-gray-100 font-semibold"
                  onClick={() => window.open('tel:0587346342', '_self')}
                >
                  <Phone className="h-5 w-5 mr-2" />
                  Chiamaci: 0587 346342
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-white text-white hover:bg-white hover:text-blue-600"
                  onClick={() => window.open('https://api.whatsapp.com/send?phone=3477030346', '_blank')}
                >
                  <Mail className="h-5 w-5 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RifacimentiBagniSection;